import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import React from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../contexts/auth-context';
import { useAllianceIdentity } from '../hooks/use-alliance-identity';
import { useGettingStarted } from '../hooks/use-getting-started';
import { AgtStaffPermissions, GettingStartedHomeData } from '../types';
import { BarButton, useIsMobileLayout } from './alliance/bar-controls';
import { BarMenu, MENU_COLORS } from './alliance/bar-menu';
import { CURRENT_APP } from './alliance/current-app';
import { SectionErrorScreen } from './alliance/system-screens';
import { SectionErrorBoundary } from './section-error-boundary';

/**
 * The eight Getting Started steps, in order (copy from the App Bar design).
 * `done` reads the /agent/getting-started flag; Fastract is always done
 * (an agent number means registration finished). `to` is an in-app route,
 * `href` an external page; steps with neither have no page.
 */
const STEPS: {
  key: string;
  title: string;
  desc: string;
  done: (data: GettingStartedHomeData) => boolean;
  to?: string;
  href?: string;
  staffPermission?: AgtStaffPermissions;
}[] = [
  {
    key: 'fastract',
    title: 'Fastract Registration',
    desc: 'Getting your NAA#',
    done: () => true,
  },
  {
    key: 'agreement',
    title: 'Agent Agreement',
    desc: 'View and complete the Agent Agreement',
    done: (data) => Boolean(data.HasSignedICA),
    // Home opens the Agreement dialog
    to: '/home?open=agreement',
  },
  {
    key: 'licenses',
    title: 'Licenses',
    desc: 'Add the states you are licensed to sell in',
    done: (data) => Boolean(data.HasLicenses),
    to: '/licenses',
  },
  {
    key: 'w9',
    title: 'Tax Document (W-9)',
    desc: 'Complete your W-9 tax form',
    done: (data) => Boolean(data.HasCompletedW9),
    // Home opens the W-9 dialog
    to: '/home?open=w9',
  },
  {
    key: 'carrier',
    title: 'Carrier Contracting',
    desc: 'Complete carrier contracts to get a writing #',
    done: (data) => Boolean(data.HasCarrierContract),
    to: '/requests',
  },
  {
    key: 'wallet',
    title: 'Your Wallet',
    desc: 'Account for Mon, Wed & Fri deposits',
    done: (data) => Boolean(data.HasCommDepositInfo),
    to: '/wallet',
    staffPermission: AgtStaffPermissions['Wallet'],
  },
  {
    key: 'policy',
    title: 'Personal Use Policy',
    desc: 'Issue a policy on you or a family member with an Alliance product',
    done: (data) => Boolean(data.HasPersIPPaidPolicy),
  },
  {
    key: 'deposit',
    title: 'ARC Deposit',
    desc: 'Congrats! You got paid!',
    done: (data) => Boolean(data.Has1stARCDeposit),
    href: 'https://arc.naaleads.com/business/commissions',
  },
];

// Circumference of the r=15 progress ring
const RING = 94.25;

/** The ring on the bar control: done/total of the circle in the accent */
function ProgressRing(props: { done: number; total: number }) {
  const dash = ((props.done / props.total) * RING).toFixed(1);
  return (
    <Box
      component="svg"
      width={24}
      height={24}
      viewBox="0 0 36 36"
      aria-hidden="true"
      sx={{ flex: 'none' }}
    >
      <circle
        cx="18"
        cy="18"
        r="15"
        fill="none"
        stroke="#ffffff33"
        strokeWidth="4"
      />
      <circle
        cx="18"
        cy="18"
        r="15"
        fill="none"
        stroke={CURRENT_APP.accentOnBar}
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${RING}`}
        transform="rotate(-90 18 18)"
      />
    </Box>
  );
}

/**
 * Whether the App Bar shows Getting started: once the first status arrives
 * (or the fetch failed, so the error is reachable), until all steps are done.
 * Reads the same query as the control, so it adds no request.
 *
 * @param enabled - Pass false where the bar never shows it (full-width pages)
 */
export function useShowGettingStarted(params: { enabled: boolean }) {
  // Hooks
  const { query } = useGettingStarted({ enabled: params.enabled });

  if (!params.enabled) {
    return false;
  } else if (query.data) {
    return STEPS.some((step) => query.data && !step.done(query.data));
  }
  // Failed before any status arrived. errorUpdatedAt stays set while a retry
  // is in flight, so the control doesn't unmount and remount (which would
  // retry again) in a loop
  return query.errorUpdatedAt > 0;
}

/**
 * Getting started, in the App Bar's actions slot (Contracting only).
 *
 * Shows for agents, agent staff (their agent's steps) and anyone
 * impersonating (that agent's steps); hidden for admins who aren't, and once
 * all eight steps are done (the steps stay on Home). The control is a
 * progress ring with "n of 8"; it opens a panel listing the steps, with the
 * first incomplete one marked Next up and open steps linking to their page.
 *
 * Step status is fetched on mount, when the panel opens and when a step is
 * clicked; never on close. If a fetch fails the last progress stays and the
 * panel shows the compact section error.
 */
export function GettingStarted() {
  // Context
  const {
    state: { session },
  } = React.useContext(AuthContext);
  // State
  const [open, setOpen] = React.useState(false);
  // Hooks
  const isMobile = useIsMobileLayout();
  const identity = useAllianceIdentity();
  // The shell's useShowGettingStarted already fetched on mount
  const { query } = useGettingStarted({ refetchOnMount: false });

  const accessToken = session?.getAccessToken().decodePayload();
  const isAgtStaff = Boolean(accessToken?.['myStaffForAgt']);
  const staffPerms: string[] = accessToken?.['cognito:groups'] || [];

  const { refetch } = query;
  const handleOpen = () => {
    setOpen(true);
    refetch();
  };
  const handleStep = () => {
    setOpen(false);
    refetch();
  };

  // Nothing to show until the first status arrives, unless it failed (then
  // the control stays so the error is reachable)
  if (!query.data && !query.errorUpdatedAt) {
    return null;
  }

  const steps = STEPS.map((step) => ({
    ...step,
    isDone: query.data ? step.done(query.data) : false,
    // Staff without access to that page get no link to it
    linkable: !(
      isAgtStaff &&
      step.staffPermission &&
      !staffPerms.includes(step.staffPermission)
    ),
  }));
  const total = steps.length;
  const done = steps.filter((step) => step.isDone).length;
  const nextIndex = steps.findIndex((step) => !step.isDone);
  // False only when the very first fetch failed: no count, no step list
  const hasStatus = Boolean(query.data);

  if (query.data && done === total) {
    return null;
  }

  // Name the agent whose steps these are when it isn't the user
  const other = identity?.impersonating || identity?.staffFor || null;

  // API messages arrive on error.response.data.error
  const errorMessage: string | null =
    // @ts-ignore
    query.error?.response?.data?.error || null;

  return (
    <React.Fragment>
      <BarButton
        id="getting-started-button"
        open={open}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={open ? 'getting-started-menu' : undefined}
        aria-label={
          hasStatus
            ? `Getting started, ${done} of ${total} done`
            : 'Getting started'
        }
        onClick={handleOpen}
        sx={{
          gap: 1,
          px: isMobile ? '7px' : '12px',
          pl: isMobile ? '7px' : '8px',
        }}
      >
        <ProgressRing done={done} total={total} />
        {isMobile ? null : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '2px',
              lineHeight: 1.1,
            }}
          >
            <Box sx={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}>
              Getting started
            </Box>
            {hasStatus ? (
              <Box
                sx={{
                  fontFamily: "'Roboto Mono', monospace",
                  fontSize: 11,
                  fontWeight: 700,
                  color: CURRENT_APP.accentOnBar,
                }}
              >
                {done} of {total}
              </Box>
            ) : null}
          </Box>
        )}
      </BarButton>

      <BarMenu
        id="getting-started-menu"
        label="Getting started"
        width={380}
        open={open}
        onClose={() => setOpen(false)}
      >
        {/* A render error in the panel stays in the panel */}
        <SectionErrorBoundary dense>
          <Box
            sx={{
              p: '16px 18px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <Box sx={{ fontSize: 18, fontWeight: 500 }}>Getting started</Box>
              {hasStatus ? (
                <Box
                  sx={{
                    fontFamily: "'Roboto Mono', monospace",
                    fontSize: 12,
                    fontWeight: 700,
                    color: MENU_COLORS.accent,
                  }}
                >
                  {done} of {total} done
                </Box>
              ) : null}
            </Box>

            {hasStatus ? (
              <Box
                role="progressbar"
                aria-label="Steps done"
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={done}
                sx={{
                  height: 6,
                  borderRadius: '3px',
                  backgroundColor: MENU_COLORS.divider,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${Math.round((done / total) * 100)}%`,
                    backgroundColor: CURRENT_APP.accent,
                    borderRadius: '3px',
                  }}
                />
              </Box>
            ) : null}

            {other ? (
              <Box sx={{ fontSize: 13, color: MENU_COLORS.text2 }}>
                Steps for{' '}
                <Box
                  component="b"
                  sx={{ color: MENU_COLORS.text, fontWeight: 700 }}
                >
                  {other.fullName}
                </Box>{' '}
                <Box
                  component="span"
                  sx={{
                    fontFamily: "'Roboto Mono', monospace",
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  {other.id}
                </Box>
              </Box>
            ) : null}
          </Box>

          {query.isError ? (
            <Box sx={{ px: 1, pb: 1 }}>
              <SectionErrorScreen
                dense
                message={errorMessage}
                onRetry={() => refetch()}
              />
            </Box>
          ) : null}

          {hasStatus ? (
            <Box
              component="ol"
              sx={{
                listStyle: 'none',
                m: 0,
                p: '0 8px 8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              {steps.map((step, index) => (
                <Box component="li" key={step.key}>
                  <StepRow
                    title={step.title}
                    desc={step.desc}
                    isDone={step.isDone}
                    isNext={index === nextIndex}
                    to={step.linkable && !step.isDone ? step.to : undefined}
                    href={step.linkable && !step.isDone ? step.href : undefined}
                    onClick={handleStep}
                  />
                </Box>
              ))}
            </Box>
          ) : null}

          <Box sx={{ height: '1px', backgroundColor: MENU_COLORS.divider }} />
          <Box
            sx={{
              p: '12px 18px',
              fontSize: 13,
              lineHeight: 1.45,
              color: MENU_COLORS.text2,
            }}
          >
            When every step is done, talk to your upline about what's next. This
            button leaves the bar once all steps are complete.
          </Box>
        </SectionErrorBoundary>
      </BarMenu>
    </React.Fragment>
  );
}

/**
 * One step in the panel. Open steps with a page are links (with a chevron);
 * done steps and steps with no page are plain rows. The Next up step is
 * tinted in the accent.
 */
function StepRow(props: {
  title: string;
  desc: string;
  isDone: boolean;
  isNext: boolean;
  to?: string;
  href?: string;
  onClick: () => void;
}) {
  // Props
  const { isDone, isNext } = props;

  const isLink = Boolean(props.to || props.href);
  let linkProps: object = { component: 'div', disableRipple: true };
  if (props.to) {
    linkProps = { component: Link, to: props.to, onClick: props.onClick };
  } else if (props.href) {
    linkProps = {
      component: 'a',
      href: props.href,
      target: '_blank',
      rel: 'noopener noreferrer',
      onClick: props.onClick,
    };
  }

  return (
    <ButtonBase
      {...linkProps}
      tabIndex={isLink ? 0 : -1}
      sx={{
        fontFamily: 'inherit',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '12px',
        p: '8px 10px',
        borderRadius: '10px',
        textAlign: 'left',
        lineHeight: 1.3,
        color: MENU_COLORS.text,
        cursor: isLink ? 'pointer' : 'default',
        backgroundColor: isNext ? `${CURRENT_APP.accent}14` : 'transparent',
        border: `1px solid ${
          isNext ? `${CURRENT_APP.accent}66` : 'transparent'
        }`,
        '&:hover': {
          backgroundColor: isNext
            ? `${CURRENT_APP.accent}14`
            : isLink
              ? MENU_COLORS.hover
              : 'transparent',
        },
      }}
    >
      {isDone ? (
        <CheckCircleIcon
          aria-label="Done"
          sx={{ fontSize: 22, color: CURRENT_APP.accent, flex: 'none' }}
        />
      ) : (
        <RadioButtonUncheckedIcon
          aria-label="Not done"
          sx={{ fontSize: 22, color: MENU_COLORS.text2, flex: 'none' }}
        />
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3 }}>
            {props.title}
          </Box>
          {isNext ? (
            <Box
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                borderRadius: '6px',
                p: '2px 6px',
                backgroundColor: CURRENT_APP.accent,
                color: CURRENT_APP.bar,
              }}
            >
              Next up
            </Box>
          ) : null}
        </Box>
        <Box sx={{ fontSize: 12, lineHeight: 1.3, color: MENU_COLORS.text2 }}>
          {props.desc}
        </Box>
      </Box>

      {isLink ? (
        <ChevronRightIcon sx={{ fontSize: 20, color: MENU_COLORS.text2 }} />
      ) : null}
    </ButtonBase>
  );
}
